import { useEffect } from 'react';
import { useLocation } from 'react-router';

const JSON_LD_ID = 'flea-seo-jsonld';

/**
 * SEO component: title, meta, Open Graph, Twitter, canonical, optional JSON-LD.
 */
const SEO = ({
  title = 'Flea Marketplace Egypt | Buy, Sell & Exchange Pre-Owned Items',
  description = "Flea is Egypt's online marketplace for second-hand, thrift, and pre-loved items. Buy and sell fashion, furniture, books, home goods, and more.",
  keywords = 'flea marketplace Egypt, flea market Egypt, second hand Egypt, thrift Egypt, used items Egypt, pre-loved items Cairo',
  image = '/images/flea-logo.png',
  url = '',
  type = 'website',
  jsonLd = null,
  noindex = false,
  locale = 'en_EG',
  alternates = [],
}) => {
  const location = useLocation();
  const baseUrl = 'https://flea-marketplace.com';
  const fullUrl = url ? (url.startsWith('http') ? url : `${baseUrl}${url}`) : `${baseUrl}${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image.startsWith('http') ? image : `${baseUrl}${image}`, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Flea Marketplace', true);
    updateMetaTag('og:locale', locale, true);

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image.startsWith('http') ? image : `${baseUrl}${image}`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    document.querySelectorAll('link[data-flea-hreflang="true"]').forEach((element) => element.remove());
    alternates.forEach(({ hrefLang, href }) => {
      if (!hrefLang || !href) return;
      const alternate = document.createElement('link');
      alternate.setAttribute('rel', 'alternate');
      alternate.setAttribute('hreflang', hrefLang);
      alternate.setAttribute('href', href.startsWith('http') ? href : `${baseUrl}${href}`);
      alternate.dataset.fleaHreflang = 'true';
      document.head.appendChild(alternate);
    });

    const schemas = jsonLd
      ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean)
      : [];
    const existing = document.getElementById(JSON_LD_ID);
    if (existing) existing.remove();
    if (schemas.length) {
      const script = document.createElement('script');
      script.id = JSON_LD_ID;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(JSON_LD_ID);
      if (el) el.remove();
      document.querySelectorAll('link[data-flea-hreflang="true"]').forEach((element) => element.remove());
    };
  }, [title, description, keywords, image, fullUrl, type, jsonLd, noindex, locale, alternates]);

  return null;
};

export default SEO;
